import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ConversationsPanel.tsx -- cross-session conversation history.
//
// Per workbench I3: lists every chat session the current user has in
// this project (default-FDN OR coworker-anchored), newest first. Click
// a row to resume that session. Search input filters client-side over
// title + preview + coworker name.
//
// Companion to CoworkerHistoryPopover -- that one filters to a single
// coworker; this one is the cross-cutting "all conversations" view
// the user reaches via + > Conversations from anywhere on the
// desktop, regardless of which coworker (if any) is currently active.
//
// Reuses .recipient-history-* CSS for the list shape; the only
// genuinely new visual is the coworker badge on rows whose session
// is anchored.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../utils/api.js';
import { useChatStore } from '../../hooks/useAgent.js';
import { useAuth } from '../../hooks/useAuth.js';
function getInitials(s) {
    if (!s)
        return '?';
    const parts = s.trim().split(/\s+/);
    if (parts.length === 1)
        return s.slice(0, 2).toUpperCase();
    return (parts[0][0] ?? '?').toUpperCase() + (parts[parts.length - 1][0] ?? '').toUpperCase();
}
export function ConversationsPanel({ projectId, open, onClose }) {
    const { user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [coworkerById, setCoworkerById] = useState(new Map());
    const [memberById, setMemberById] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [filter, setFilter] = useState('');
    const panelRef = useRef(null);
    // Fetch sessions on open. Then resolve any coworkerIds we don't
    // already have cached. Both calls are best-effort -- a coworker
    // lookup failing just leaves that row badge-less.
    const refresh = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/p/${projectId}/chat/sessions`);
            const list = r.sessions ?? [];
            setSessions(list);
            // Resolve member identities once -- the project's member list is
            // small and the panel needs them for human-DM session chips.
            // Best-effort: a 403/404 leaves rows un-named (chip falls back
            // to userId or "Member").
            try {
                const mr = await api.get(`/p/${projectId}/members`);
                setMemberById(() => {
                    const next = new Map();
                    for (const m of mr.members ?? []) {
                        next.set(m.userId, {
                            userId: m.userId,
                            name: m.name ?? m.email ?? m.userId,
                            ...(m.email ? { email: m.email } : {}),
                        });
                    }
                    return next;
                });
            }
            catch {
                // Non-fatal -- the human-DM rows will render with userId
                // instead of name. Coworker rows are unaffected.
            }
            // Find coworkerIds we haven't resolved yet, fetch in parallel.
            // Prefer the discriminated recipient (canonical since i16);
            // fall back to legacy s.coworkerId for pre-i16 droplets.
            const ids = Array.from(new Set(list
                .map((s) => s.recipient?.kind === 'coworker'
                ? s.recipient.coworkerId
                : s.coworkerId)
                .filter((id) => Boolean(id) && !coworkerById.has(id ?? ''))));
            if (ids.length > 0) {
                const fetched = await Promise.all(ids.map(async (id) => {
                    try {
                        const cw = await api.get(`/p/${projectId}/coworkers/${id}`);
                        return cw.coworker;
                    }
                    catch {
                        return null;
                    }
                }));
                setCoworkerById((prev) => {
                    const next = new Map(prev);
                    for (const cw of fetched) {
                        if (cw) {
                            next.set(cw.coworkerId, {
                                coworkerId: cw.coworkerId,
                                name: cw.name,
                                ...(cw.avatarEmoji ? { avatarEmoji: cw.avatarEmoji } : {}),
                            });
                        }
                    }
                    return next;
                });
            }
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load conversations');
        }
        finally {
            setLoading(false);
        }
    }, [projectId, coworkerById]);
    useEffect(() => {
        if (open) {
            void refresh();
            setFilter('');
        }
        // refresh deps include coworkerById which changes when we
        // resolve new coworkers; re-running on open is what we want
        // (cheap; same-tenant index walk).
    }, [open, refresh]);
    // Outside click + Escape close.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target))
                onClose();
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);
    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q)
            return sessions;
        return sessions.filter((s) => {
            if ((s.title ?? '').toLowerCase().includes(q))
                return true;
            if ((s.lastMessagePreview ?? '').toLowerCase().includes(q))
                return true;
            // Search by coworker name -- prefer discriminated recipient,
            // fall back to legacy s.coworkerId for pre-i16 droplets.
            const cwId = s.recipient?.kind === 'coworker'
                ? s.recipient.coworkerId
                : s.coworkerId;
            const cw = cwId ? coworkerById.get(cwId) : null;
            if (cw && cw.name.toLowerCase().includes(q))
                return true;
            // Search by member name for human DMs.
            if (s.recipient?.kind === 'user' && user) {
                const recipientUserId = s.recipient.userId;
                const otherId = s.userId === user.userId
                    ? recipientUserId
                    : recipientUserId === user.userId
                        ? s.userId
                        : recipientUserId;
                const m = otherId ? memberById.get(otherId) : null;
                if (m && m.name.toLowerCase().includes(q))
                    return true;
            }
            return false;
        });
    }, [sessions, filter, coworkerById, memberById, user]);
    /**
     * Derive the recipient discriminator from a session row + caches,
     * resolved from the VIEWER's perspective (workbench symmetric-DM
     * contract from May 3 commit b325cba "complete I11 symmetric DM
     * flow", restored after the v2 schema reshape + lightning port
     * lost the cross-user-symmetry logic).
     *
     * Resolution order:
     *   1. server's discriminated `recipient` field (canonical since
     *      i16 commit 8994106). For coworker-recipient sessions, attach
     *      the coworker's cached display name + avatar. For human-DM
     *      sessions, "the other party" depends on viewer-vs-owner:
     *        - viewer === session.owner (s.userId) -> recipient.userId
     *          is literally the other party. Use it.
     *        - viewer !== session.owner -> viewer IS the recipient
     *          (recipient.userId === viewer.userId). The "other party"
     *          from the viewer's seat is the OWNER (s.userId).
     *      Without this swap, the recipient-side user sees "Chat with
     *      myself" in the row chip and can't tell who the DM is from.
     *   2. Legacy fallback for sessions written before i16 -- coworkerId
     *      (denormalised) gates a coworker-recipient resolution. Kept
     *      because old droplets in long-lived tenants still validate
     *      against v1 and don't carry the recipient field.
     *   3. Default-FDN session (no recipient and no coworker hint).
     */
    const recipientForSession = useCallback((s) => {
        // Path 1a: discriminated recipient -- coworker.
        if (s.recipient?.kind === 'coworker') {
            const cw = coworkerById.get(s.recipient.coworkerId);
            return {
                kind: 'coworker',
                coworkerId: s.recipient.coworkerId,
                name: cw?.name ?? 'Co-worker',
                ...(cw?.avatarEmoji ? { avatarEmoji: cw.avatarEmoji } : {}),
            };
        }
        // Path 1b: discriminated recipient -- human DM (viewer-perspective swap).
        if (s.recipient?.kind === 'user' && user) {
            const recipientUserId = s.recipient.userId;
            // If viewer IS the owner, recipient.userId IS the other party.
            // If viewer IS the recipient, "the other party" is the owner.
            const otherId = s.userId === user.userId
                ? recipientUserId
                : recipientUserId === user.userId
                    ? s.userId
                    : recipientUserId;
            if (otherId) {
                const m = memberById.get(otherId);
                return {
                    kind: 'user',
                    userId: otherId,
                    name: m?.name ?? otherId,
                    ...(m?.email ? { email: m.email } : {}),
                };
            }
        }
        // Path 2: legacy v1 sessions (no recipient field).
        if (s.coworkerId) {
            const cw = coworkerById.get(s.coworkerId);
            return {
                kind: 'coworker',
                coworkerId: s.coworkerId,
                name: cw?.name ?? 'Co-worker',
                ...(cw?.avatarEmoji ? { avatarEmoji: cw.avatarEmoji } : {}),
            };
        }
        // Path 3: default FDN assistant.
        return { kind: 'fdn' };
    }, [coworkerById, memberById, user]);
    const onResume = useCallback((s) => {
        const recipient = recipientForSession(s);
        useChatStore.getState().openWindow({
            recipient,
            sessionId: s.sessionId,
        });
        onClose();
    }, [recipientForSession, onClose]);
    const onNewDefault = useCallback(() => {
        useChatStore.getState().openWindow({ recipient: { kind: 'fdn' } });
        onClose();
    }, [onClose]);
    if (!open)
        return null;
    return (_jsx("div", { className: "modal-bg", onClick: onClose, children: _jsxs("div", { ref: panelRef, className: "conversations-panel", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "coworkers-header", children: [_jsx("h3", { children: "Conversations" }), _jsx("button", { type: "button", className: "ghost", onClick: onClose, title: "Close", children: '\u2715' })] }), _jsx("div", { className: "recipient-history-search", children: _jsx("input", { autoFocus: true, value: filter, onChange: (e) => setFilter(e.target.value), placeholder: 'Filter by title, preview, or co-worker\u2026' }) }), _jsxs("div", { className: "recipient-history-body conversations-body", children: [loading && _jsx("div", { className: "recipient-history-empty", children: 'Loading\u2026' }), err && _jsx("div", { className: "error", style: { margin: 12 }, children: err }), !loading && !err && sessions.length === 0 && (_jsxs("div", { className: "recipient-history-empty", children: [_jsx("p", { children: "No conversations yet." }), _jsxs("p", { style: { color: 'var(--fg-muted)', fontSize: 12 }, children: ["Click anywhere on the desktop to start chatting with the FDN assistant,", ' ', "or pick a co-worker from the +\u00A0menu."] })] })), !loading && !err && sessions.length > 0 && filtered.length === 0 && (_jsx("div", { className: "recipient-history-empty", children: _jsxs("p", { style: { color: 'var(--fg-muted)' }, children: ["No matches for \"", filter, "\"."] }) })), !loading && filtered.map((s) => {
                            // In the multi-window era, "active" is ambiguous (multiple
                            // windows could have the same session). The "current"
                            // affordance is dropped; clicking always opens a NEW
                            // window for that session.
                            const active = false;
                            // Coworker affordance: prefer discriminated recipient,
                            // fall back to legacy coworkerId for pre-i16 sessions.
                            const coworkerHintId = s.recipient?.kind === 'coworker'
                                ? s.recipient.coworkerId
                                : s.coworkerId;
                            const cw = coworkerHintId ? coworkerById.get(coworkerHintId) : null;
                            // Compute "the other party" for human DMs from the
                            // VIEWER's perspective. Same logic as recipientForSession
                            // (kept in sync intentionally -- this site renders the row
                            // chip, that site builds the resume payload).
                            //
                            // - viewer === owner: recipient.userId is literally the
                            //   other party.
                            // - viewer === recipient: "the other party" from viewer's
                            //   seat is the owner.
                            //
                            // The pre-fix code read s.participants expecting the v1
                            // pre-i16 Array<{type,id}> shape and called
                            // .filter(p => p.type === 'user') -- but the current
                            // server sends participants: string[] | null (workbench
                            // I19 visibility lock, NOT recipient list). The filter
                            // returned [] for every human DM, the otherId chain
                            // collapsed, and "other party" never resolved on the
                            // recipient's seat (asymmetric DM regression).
                            let otherMember = null;
                            if (!cw && user && s.recipient?.kind === 'user') {
                                const recipientUserId = s.recipient.userId;
                                const otherId = s.userId === user.userId
                                    ? recipientUserId
                                    : recipientUserId === user.userId
                                        ? s.userId
                                        : recipientUserId;
                                if (otherId)
                                    otherMember = memberById.get(otherId) ?? null;
                            }
                            const chipKind = cw
                                ? 'coworker'
                                : otherMember ? 'member' : 'fdn';
                            // coworkerHintId covers both discriminated recipient.kind:'coworker'
                            // and the legacy s.coworkerId fallback.
                            const isCoworkerSession = !!coworkerHintId;
                            const chipName = cw?.name
                                ?? otherMember?.name
                                ?? (isCoworkerSession ? 'Unknown coworker' : 'FDN Assistant');
                            const chipGlyph = cw?.avatarEmoji
                                ?? (otherMember
                                    ? null // member uses initials, see render below
                                    : (isCoworkerSession ? '\uD83E\uDD16' : '\u2728'));
                            return (_jsxs("button", { type: "button", className: `recipient-history-row ${active ? 'active' : ''}`, onClick: () => onResume(s), title: `Resume "${s.title ?? 'Untitled'}"`, children: [_jsxs("div", { className: "conversations-row-head", children: [chipKind === 'member' && otherMember ? (_jsx("span", { className: "conversations-row-coworker-chip member-avatar", "aria-hidden": true, children: getInitials(otherMember.name) })) : (_jsx("span", { className: "conversations-row-coworker-chip", "aria-hidden": true, children: chipGlyph })), _jsx("span", { className: "conversations-row-coworker-name", children: chipName })] }), _jsxs("div", { className: "recipient-history-row-title", children: [s.title ?? 'Untitled conversation', active && _jsx("span", { className: "recipient-history-row-current", title: "Currently open", children: ' \u2022 current' })] }), _jsxs("div", { className: "recipient-history-row-meta", children: [formatRelative(s.lastMessageAt), typeof s.messageCount === 'number' && s.messageCount > 0 && (_jsxs("span", { children: [" ", '\u00b7', " ", s.messageCount, " message", s.messageCount === 1 ? '' : 's'] }))] }), s.lastMessagePreview && (_jsx("div", { className: "recipient-history-row-preview", children: s.lastMessagePreview.slice(0, 160) }))] }, s.sessionId));
                        })] }), _jsx("div", { className: "recipient-history-footer", children: _jsx("button", { type: "button", onClick: onNewDefault, children: '+ New conversation' }) })] }) }));
}
function formatRelative(iso) {
    if (!iso)
        return 'never';
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t))
        return iso;
    const dMs = Date.now() - t;
    const s = Math.round(dMs / 1000);
    if (s < 60)
        return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60)
        return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24)
        return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7)
        return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
}
